import { createContext, useContext } from "react";
import type { Dispatch, SetStateAction } from "react";
import { FileGenerationStatus } from "catchment-common/entities/fileGeneration";

/** One in-flight file generation, as the provider tracks it. */
export interface DownloadState {
  id: string;
  downloadStep: FileGenerationStatus;
  downloadProgress: number;
  error: string | null;
  title: string;
  downloadKey: string;
}

export interface PdfDownloadContextType {
  downloads: DownloadState[];
  setDownloads: Dispatch<SetStateAction<DownloadState[]>>;
}

/**
 * The context and its reader, split out from the provider that fills it.
 *
 * The provider shows a toast for every download, and the toast's content
 * subscribes to this context for live progress — so provider and toast imported
 * each other, and the pair was one of the two real runtime cycles left in the
 * app on 2026-08-26. Nothing was wrong with either half: a toast that reports
 * progress has to read progress from somewhere.
 *
 * Splitting the module is what breaks it. This file imports nothing of ours;
 * the toast reads the context from here, and the provider — which imports the
 * toast — sits one level above both. `usePdfDownloadContext` re-exports these,
 * so no consumer has to know.
 */
export const PdfDownloadContext = createContext<PdfDownloadContextType>({
  downloads: [],
  setDownloads: () => {}
});

export const usePdfDownloadContext = () => useContext(PdfDownloadContext);
